import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { MAIN_HEADER_HEIGHT } from 'components/common/NavbarWrapper'
import { SIDE_NAV_DRAWER_WIDTH } from 'components/common/side-nav/settings'
import { closeDrawer, openDrawer, selectIsNotificationDrawerOpen } from 'slices/notification-center/notificationDrawerSlice'
import T from 'T'

import { type NotificationView } from './notificationCenterViews'
import NotificationViewItem from './NotificationViewItem'
import { useNotificationLiveSet } from './useNotificationLiveSet'

type NotificationCenterDrawerProps = {
  unreadCount: number
  activeViewKey: string
  onSelectView: (viewKey: string) => void
}

type ViewGroupProps = {
  groupName: string
  views: readonly NotificationView[]
  unreadCount: number
  activeViewKey: string
  onSelectView: (viewKey: string) => void
}

const ViewGroup = ({ groupName, views, unreadCount, activeViewKey, onSelectView }: ViewGroupProps) => {
  const [isOpen, setIsOpen] = useState(true)

  const handleToggle = useCallback(() => setIsOpen(open => !open), [])

  return (
    <List disablePadding>
      <ListItemButton onClick={handleToggle}>
        <ListItemIcon sx={{ minWidth: 32 }}>{isOpen ? <ExpandLess /> : <ExpandMore />}</ListItemIcon>
        <ListItemText primary={groupName} slotProps={{ primary: { variant: 'h6', fontWeight: 500 } }} />
      </ListItemButton>
      <Divider />
      {isOpen &&
        views.map(view => (
          <NotificationViewItem
            key={view.key}
            activeViewKey={activeViewKey}
            unreadCount={unreadCount}
            view={view}
            onSelectView={onSelectView}
          />
        ))}
    </List>
  )
}

/**
 * Left side-nav of notification views, following the pricing/inventory drawer
 * pattern — persistent on desktop, temporary below `lg`, offset by the fixed
 * app bar, and each view favouritable so it can become the landing URL.
 */
const NotificationCenterDrawer = ({ unreadCount, activeViewKey, onSelectView }: NotificationCenterDrawerProps) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const isOpen = useSelector(selectIsNotificationDrawerOpen)
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const { viewGroups } = useNotificationLiveSet()

  const handleOpen = useCallback(() => dispatch(openDrawer()), [dispatch])

  const handleClose = useCallback(() => dispatch(closeDrawer()), [dispatch])

  useEffect(() => {
    dispatch(isTabletOrMobile ? closeDrawer() : openDrawer())
  }, [dispatch, isTabletOrMobile])

  return (
    <SwipeableDrawer
      anchor="left"
      open={isOpen}
      sx={{
        width: SIDE_NAV_DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDE_NAV_DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: MAIN_HEADER_HEIGHT,
        },
      }}
      variant={isTabletOrMobile ? 'temporary' : 'persistent'}
      onClose={handleClose}
      onOpen={handleOpen}
    >
      <ListItem
        secondaryAction={
          <IconButton edge="end" onClick={handleClose}>
            <ChevronLeftIcon />
          </IconButton>
        }
        sx={{ height: MAIN_HEADER_HEIGHT, borderBottom: 1, borderColor: 'divider' }}
      >
        <ListItemText primary={T.NOTIFICATIONS} slotProps={{ primary: { variant: 'h5', fontWeight: 500 } }} />
      </ListItem>

      {viewGroups.map(group => (
        <ViewGroup
          key={group.groupName}
          activeViewKey={activeViewKey}
          groupName={group.groupName}
          unreadCount={unreadCount}
          views={group.views}
          onSelectView={onSelectView}
        />
      ))}
    </SwipeableDrawer>
  )
}

export default NotificationCenterDrawer
